/**
 * Social Login Wrapper
 * Waits for #fbLogin and #googleLogin elements to be present,
 * then creates fake buttons that trigger the real ones programmatically
 * while keeping the original elements hidden but functional.
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    googleSelector: '#googleLogin',
    facebookSelector: '#fbLogin',
    wrapperClass: 'social-login-wrapper',
    wrapperId: 'socialLoginContainer',
    checkInterval: 100, // Check every 100ms
    maxAttempts: 50, // Maximum attempts (5 seconds)
    debug: true // Set to false in production
  };

  let attempts = 0;
  let isWrapped = false;
  let realGoogleElement = null;
  let realFacebookElement = null;
  
  // Window-level variable to track screen transitions
  window.smlScreenHistory = window.smlScreenHistory || [];
  window.smlInitialScreen = window.smlInitialScreen || null;

  /**
   * Move #loginError element after .selectbox-wrapper
   */
  function moveLoginError() {
    const loginError = document.getElementById('loginError');
    const selectboxWrapper = document.querySelector('.selectbox-wrapper');
    
    if (loginError && selectboxWrapper) {
      // Check if already in correct position
      if (loginError.previousElementSibling === selectboxWrapper) {
        return; // Already in correct position
      }
      
      // Move the error element after selectbox-wrapper
      selectboxWrapper.parentNode.insertBefore(loginError, selectboxWrapper.nextSibling);
      log('Moved #loginError after .selectbox-wrapper');
    }
  }

  /**
   * Move #smlEmailOTPDivError element after #smlEmailOTPDivUserEmailInput
   */
  function moveEmailOTPError() {
    const emailError = document.getElementById('smlEmailOTPDivError');
    const emailInput = document.getElementById('smlEmailOTPDivUserEmailInput');
    
    if (emailError && emailInput) {
      // Check if already in correct position
      if (emailError.previousElementSibling === emailInput) {
        return; // Already in correct position
      }
      
      // Move the error element after email input
      emailInput.parentNode.insertBefore(emailError, emailInput.nextSibling);
      log('Moved #smlEmailOTPDivError after #smlEmailOTPDivUserEmailInput');
    }
  }

  /**
   * Intercept SML plugin hiding smlEmailOTPScreenGetOTPPage.
   * Instead of hiding the whole parent (which buries the error div),
   * hide only the email input and Get-OTP button children, keeping
   * smlEmailOTPDivError visible.
   */
  function setupGetOTPPageHideIntercept() {
    const PAGE_ID   = 'smlEmailOTPScreenGetOTPPage';
    const INPUT_ID  = 'smlEmailOTPDivUserEmailInput';
    const BUTTON_ID = 'smlEmailOTPDivGetOTPButton';

    // Guard: only install once the parent exists in the DOM
    function installObserver() {
      const page = document.getElementById(PAGE_ID);
      if (!page) return false;

      const styleObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type !== 'attributes' || mutation.attributeName !== 'style') return;

          const el = mutation.target;
          // If the plugin just set display:none on the parent…
          if (el.id === PAGE_ID && el.style.display === 'none') {
            // …reverse it and hide only the two children instead
            el.style.display = '';
            const inputEl  = document.getElementById(INPUT_ID);
            const buttonEl = document.getElementById(BUTTON_ID);
            if (inputEl)  inputEl.style.display  = 'none';
            if (buttonEl) buttonEl.style.display = 'none';
            log('Intercepted hide on #' + PAGE_ID + ' — hiding children only, keeping error visible');
          }

          // When the plugin restores the parent to visible, restore children too
          if (el.id === PAGE_ID && el.style.display !== 'none') {
            const inputEl  = document.getElementById(INPUT_ID);
            const buttonEl = document.getElementById(BUTTON_ID);
            if (inputEl)  inputEl.style.display  = '';
            if (buttonEl) buttonEl.style.display = '';
          }
        });
      });

      styleObserver.observe(page, { attributes: true, attributeFilter: ['style'] });
      log('Installed style intercept observer on #' + PAGE_ID);
      return true;
    }

    // Try immediately; if the element isn't in the DOM yet, wait for it
    if (!installObserver()) {
      const domObserver = new MutationObserver(function(mutations) {
        if (document.getElementById(PAGE_ID)) {
          domObserver.disconnect();
          installObserver();
        }
      });
      domObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  const SML_EMAIL_INVALID_MSG = 'please enter a valid email';

  /**
   * After Get OTP click: hide email input + button only if the client validation
   * error "Please enter a valid email" is NOT shown on #smlEmailOTPDivError.
   */
  function smlEmailOtpErrorShowsInvalidEmail() {
    const err = document.getElementById('smlEmailOTPDivError');
    if (!err) return false;
    const cs = window.getComputedStyle(err);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    const text = (err.textContent || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return text.indexOf(SML_EMAIL_INVALID_MSG) !== -1;
  }

  function setupGetOTPButtonConditionalHide() {
    const INPUT_ID  = 'smlEmailOTPDivUserEmailInput';
    const BUTTON_ID = 'smlEmailOTPDivGetOTPButton';

    function scheduleMaybeHide() {
      function maybeHide() {
        if (smlEmailOtpErrorShowsInvalidEmail()) {
          log('Get OTP: client email validation error visible — keeping input and button');
          return;
        }
        const input = document.getElementById(INPUT_ID);
        const button = document.getElementById(BUTTON_ID);
        if (input) input.style.display = 'none';
        if (button) button.style.display = 'none';
        log('Get OTP: no "Please enter a valid email" — hid email input and Get OTP button');
      }
      /* Defer so SML can paint #smlEmailOTPDivError first; avoid hiding before validation runs */
      setTimeout(maybeHide, 150);
    }

    document.body.addEventListener(
      'click',
      function(e) {
        const btn = e.target && e.target.closest && e.target.closest('#' + BUTTON_ID);
        if (!btn) return;
        scheduleMaybeHide();
      },
      false
    );
    log('Installed conditional hide on #' + BUTTON_ID);
  }

  /**
   * Setup observer to move error elements when they appear
   */
  function setupLoginErrorRepositioning() {
    // Check if elements already exist
    moveLoginError();
    moveEmailOTPError();
    
    // Watch for error elements being added to DOM
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is #loginError or contains it
            if (node.id === 'loginError' || (node.querySelector && node.querySelector('#loginError'))) {
              // Use setTimeout to ensure DOM is fully updated
              setTimeout(moveLoginError, 10);
            }
            
            // Check if the added node is #smlEmailOTPDivError or contains it
            if (node.id === 'smlEmailOTPDivError' || (node.querySelector && node.querySelector('#smlEmailOTPDivError'))) {
              // Use setTimeout to ensure DOM is fully updated
              setTimeout(moveEmailOTPError, 10);
            }
          }
        });
      });
      
      // Also check if error elements were modified (e.g., made visible)
      if (document.getElementById('loginError')) {
        moveLoginError();
      }
      if (document.getElementById('smlEmailOTPDivError')) {
        moveEmailOTPError();
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also check periodically in case observer misses it
    const checkInterval = setInterval(function() {
      if (document.getElementById('loginError') && document.querySelector('.selectbox-wrapper')) {
        moveLoginError();
      }
      if (document.getElementById('smlEmailOTPDivError') && document.getElementById('smlEmailOTPDivUserEmailInput')) {
        moveEmailOTPError();
      }
    }, 100);

    // Clear interval after 10 seconds to avoid infinite checking
    setTimeout(function() {
      clearInterval(checkInterval);
    }, 10000);
  }

  /**
   * Log debug messages
   */
  function log(message) {
    if (CONFIG.debug) {
      console.log('[Social Login Wrapper]', message);
    }
  }

  /**
   * Check if both social login elements exist
   */
  function elementsExist() {
    const googleElement = document.querySelector(CONFIG.googleSelector);
    const facebookElement = document.querySelector(CONFIG.facebookSelector);
    
    return googleElement && facebookElement;
  }

  /**
   * Create fake button that triggers the real one
   */
  function createFakeButton(realElement, buttonType) {
    const fakeButton = document.createElement('div');
    fakeButton.className = `fake-${buttonType}-button socialLogin_buttons`;
    fakeButton.style.cssText = `
      flex: 1;
      margin: 0;
      cursor: pointer;
      transition: all 0.2s ease;
    `;

    // Copy the content from the real element but modify text content
    fakeButton.innerHTML = realElement.innerHTML;
    
    // Update text content for specific spans
    const googleSpan = fakeButton.querySelector('#smlGoogleLoginButtonSpan');
    const facebookSpan = fakeButton.querySelector('#smlFacebookLoginButtonSpan');
    
    if (buttonType === 'google' && googleSpan) {
      googleSpan.innerText = 'Google';
    } else if (buttonType === 'facebook' && facebookSpan) {
      facebookSpan.innerText = 'Facebook';
    }

    // Add click handler that triggers the real element
    fakeButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      log(`Fake ${buttonType} button clicked, triggering real element`);
      
      // Programmatically click the real element
      if (realElement) {
        realElement.click();
      }
    });

    // Add hover effects
    fakeButton.addEventListener('mouseenter', function() {
      fakeButton.style.borderColor = '#cccccc';
      fakeButton.style.background = '#f8f9fa';
    });

    fakeButton.addEventListener('mouseleave', function() {
      fakeButton.style.borderColor = '#e0e0e0';
      fakeButton.style.background = '#ffffff';
    });

    return fakeButton;
  }

  /**
   * Create wrapper with fake buttons
   */
  function createSocialLoginWrapper() {
    if (isWrapped) {
      log('Elements already wrapped, skipping...');
      return;
    }

    realGoogleElement = document.querySelector(CONFIG.googleSelector);
    realFacebookElement = document.querySelector(CONFIG.facebookSelector);

    if (!realGoogleElement || !realFacebookElement) {
      log('Elements not found for wrapping');
      return;
    }

    log('Creating social login wrapper with fake buttons...');

    // Create wrapper container
    const wrapper = document.createElement('div');
    wrapper.id = CONFIG.wrapperId;
    wrapper.className = CONFIG.wrapperClass;
    wrapper.style.cssText = `
      display: flex;
      gap: 12px;
      width: 100%;
      margin: 16px 0;
    `;

    // Create fake buttons
    const fakeGoogleButton = createFakeButton(realGoogleElement, 'google');
    const fakeFacebookButton = createFakeButton(realFacebookElement, 'facebook');

    // Style fake buttons to match original
    fakeGoogleButton.style.cssText += `
      width: 100%;
      padding: 12px 16px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      background: #ffffff;
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 14px;
      color: #1a1a1a;
    `;

    fakeFacebookButton.style.cssText += `
      width: 100%;
      padding: 12px 16px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      background: #ffffff;
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 14px;
      color: #1a1a1a;
    `;

    // Append fake buttons to wrapper
    wrapper.appendChild(fakeGoogleButton);
    wrapper.appendChild(fakeFacebookButton);

    // Hide the real elements but keep them functional
    realGoogleElement.style.display = 'none';
    realFacebookElement.style.display = 'none';

    // Insert wrapper at the position of the original Google element
    const parentElement = realGoogleElement.parentNode;
    parentElement.insertBefore(wrapper, realGoogleElement);

    isWrapped = true;
    log('Social login wrapper created successfully');

    // Dispatch custom event for other scripts to listen
    const event = new CustomEvent('socialLoginWrapped', {
      detail: {
        wrapper: wrapper,
        fakeGoogleButton: fakeGoogleButton,
        fakeFacebookButton: fakeFacebookButton,
        realGoogleElement: realGoogleElement,
        realFacebookElement: realFacebookElement
      }
    });
    document.dispatchEvent(event);
  }

  /**
   * Check for elements and wrap when ready
   */
  function checkAndWrap() {
    attempts++;

    if (attempts > CONFIG.maxAttempts) {
      log('Maximum attempts reached. Social login elements not found.');
      return;
    }

    if (elementsExist()) {
      log('Social login elements found, creating wrapper...');
      createSocialLoginWrapper();
    } else {
      log(`Attempt ${attempts}: Social login elements not found, retrying...`);
      setTimeout(checkAndWrap, CONFIG.checkInterval);
    }
  }

  /**
   * Initialize the wrapper
   */
  function init() {
    log('Initializing social login wrapper...');
    
    // Start checking immediately
    checkAndWrap();

    // Also listen for DOM mutations in case elements are added dynamically
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(function(mutations) {
        if (!isWrapped && elementsExist()) {
          log('Elements detected via mutation observer, creating wrapper...');
          createSocialLoginWrapper();
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Public API
   */
  window.SocialLoginWrapper = {
    init: init,
    wrap: createSocialLoginWrapper,
    isWrapped: function() { return isWrapped; },
    getRealElements: function() { 
      return {
        google: realGoogleElement,
        facebook: realFacebookElement
      };
    },
    config: CONFIG
  };

  // Setup login error repositioning
  setupLoginErrorRepositioning();

  // Intercept SML hiding the whole GetOTP page — hide only children instead
  setupGetOTPPageHideIntercept();

  // After Get OTP click, hide input/button only when client "invalid email" error is not shown
  setupGetOTPButtonConditionalHide();

  /**
   * Show #loginWithEmailOTPButton when user clicks #loginWithPhoneNumberButton
   * (CSS keeps email OTP hidden until body gets this class.)
   */
  function revealLoginWithEmailOTPButton() {
    document.body.classList.add('sml-email-otp-button-visible');
    const el = document.getElementById('loginWithEmailOTPButton');
    if (el) {
      el.style.removeProperty('display');
      el.style.setProperty('display', 'flex', 'important');
    }
  }

  function setupRevealEmailOTPOnPhoneClick() {
    document.body.addEventListener(
      'click',
      function(e) {
        const phoneBtn = e.target && e.target.closest && e.target.closest('#loginWithPhoneNumberButton');
        if (!phoneBtn) return;
        revealLoginWithEmailOTPButton();
        log('Phone login clicked — showing #loginWithEmailOTPButton');
        /* SML may inject the button slightly after tap */
        setTimeout(revealLoginWithEmailOTPButton, 150);
        setTimeout(revealLoginWithEmailOTPButton, 400);
      },
      false
    );
  }
  setupRevealEmailOTPOnPhoneClick();

  /** Hide #loginWithEmailOTPButton after user chooses that path (class only — no inline display, so phone can show it again) */
  function setupHideEmailOTPOnSelfClick() {
    document.body.addEventListener(
      'click',
      function(e) {
        const emailOtpBtn = e.target && e.target.closest && e.target.closest('#loginWithEmailOTPButton');
        if (!emailOtpBtn) return;
        document.body.classList.remove('sml-email-otp-button-visible');
        emailOtpBtn.style.removeProperty('display');
        log('Email OTP login clicked — hid #loginWithEmailOTPButton');
      },
      false
    );
  }
  setupHideEmailOTPOnSelfClick();

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle URL hash on page load
  function handleUrlHash() {
    if (window.location.hash === '#recover') {
      showPasswordResetForm();
    }
  }

  // Check for password reset success message
  function checkForPasswordResetSuccess() {
    // Check if there's a success message in the URL or page
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('recover_success') === 'true' || 
        document.querySelector('.form__message') ||
        document.querySelector('[data-recover-success]')) {
      
      // Show success message
      showPasswordResetSuccessMessage();
    }
  }

  // Show password reset success message
  function showPasswordResetSuccessMessage() {
    // Remove any existing success message
    const existingMessage = document.querySelector('.password-reset-success');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create success message
    const successContainer = document.createElement('div');
    successContainer.className = 'password-reset-success';
    successContainer.style.cssText = `
      max-width: 400px;
      margin: 20px auto;
      padding: 16px;
      background: #d4edda;
      border: 1px solid #c3e6cb;
      border-radius: 30px;
      color: #155724;
      text-align: center;
    `;
    
    const successMessage = document.createElement('p');
    successMessage.textContent = 'Password reset email sent! Please check your inbox.';
    successMessage.style.cssText = `
      margin: 0;
      font-weight: 500;
    `;
    
    successContainer.appendChild(successMessage);
    
    // Insert after header
    const header = document.querySelector('.customer-login__header');
    if (header && header.parentNode) {
      header.parentNode.insertBefore(successContainer, header.nextSibling);
    }
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (successContainer.parentNode) {
        successContainer.remove();
      }
    }, 5000);
  }

  // Check hash on page load
  handleUrlHash();
  
  // Check for success message on page load
  checkForPasswordResetSuccess();

  // Listen for hash changes
  window.addEventListener('hashchange', handleUrlHash);

  // SML Event Handler Integration
  window.runOnSMLEvent = async (event, data) => {
    console.log("For SML event: ", event, " data is: ", data);
    
    if (event == "SCREEN_TYPE_CHANGE") {
      // Track initial screen if not set
      if (!window.smlInitialScreen) {
        window.smlInitialScreen = data.screen_type;
        console.log("Initial screen set to:", window.smlInitialScreen);
      }
      
      // Track screen transitions
      const previousScreen = window.smlScreenHistory[window.smlScreenHistory.length - 1];
      window.smlScreenHistory.push(data.screen_type);
      
      // Keep only last 3 screen transitions to prevent memory buildup
      if (window.smlScreenHistory.length > 3) {
        window.smlScreenHistory = window.smlScreenHistory.slice(-3);
      }
      
      console.log("Screen transition from:", previousScreen, "to:", data.screen_type);
      console.log("Initial screen was:", window.smlInitialScreen);
      
      if (data.screen_type == "PHONE_OTP") {
        // Hide login actions and links for PHONE_OTP screen
        hideLoginActionsAndLinks();
        adjustHeaderMargin('1.5rem');
        
        // Only reload if user did NOT start with PHONE_OTP and is now coming to PHONE_OTP
        if (window.smlInitialScreen !== "PHONE_OTP" && previousScreen && previousScreen !== "PHONE_OTP") {
          console.log("PHONE_OTP screen detected - refreshing page (started with:", window.smlInitialScreen, ", transition from:", previousScreen, ")");
          window.location.reload();
        } else if (window.smlInitialScreen === "PHONE_OTP") {
          console.log("PHONE_OTP screen detected - user started with PHONE_OTP, no reload needed");
        } else if (!previousScreen) {
          console.log("PHONE_OTP screen detected - initial page load, not refreshing");
        } else {
          console.log("PHONE_OTP screen detected - already on PHONE_OTP, not refreshing");
        }
      }
      
      if (data.screen_type == "EMAIL_PASSWORD") {
        // Show login heading and forgot password link
        console.log("EMAIL_PASSWORD screen detected - showing forgot password link");
        showForgotPasswordLink();
        showLoginActionsAndLinks();
        adjustHeaderMargin('0px');
      }
      
      if (data.screen_type == "EMAIL_OTP") {
        // Hide login actions and links
        console.log("EMAIL_OTP screen detected - hiding login actions and links");
        hideLoginActionsAndLinks();
        adjustHeaderMargin('0px');
      }
    }
  };

  /**
   * Adjust header margin based on screen type
   */
  function adjustHeaderMargin(marginValue) {
    const headers = document.querySelectorAll('.customer-login__header');
    headers.forEach(header => {
      header.style.marginBottom = marginValue;
    });
    console.log('Header margin adjusted to:', marginValue);
  }

  /**
   * Show forgot password link
   */
  function showForgotPasswordLink() {
    const forgotPasswordLinks = document.querySelectorAll('.customer-login__link');
    forgotPasswordLinks.forEach(link => {
      if (link.innerText.toLowerCase().includes('forgot your password') || 
          link.innerText.toLowerCase().includes('forgot password')) {
        link.style.display = 'block';
        
        // Add click handler for custom password reset
        link.addEventListener('click', function(e) {
          e.preventDefault();
          showPasswordResetForm();
        });
      }
    });
  }

  /**
   * Show custom password reset form
   */
  function showPasswordResetForm() {
    // Update URL
    if (!window.location.hash.includes('#recover')) {
      window.location.hash = '#recover';
    }
    
    // Hide login form and show reset form
    const loginForm = document.querySelector('.customer-login__form');
    const loginHeader = document.querySelector('.customer-login__header');
    const loginActions = document.querySelector('.customer-login__actions');
    const loginLinks = document.querySelector('.customer-login__links');
    
    if (loginForm) loginForm.style.display = 'none';
    if (loginActions) loginActions.style.display = 'none';
    if (loginLinks) loginLinks.style.display = 'none';
    
    // Update header
    if (loginHeader) {
      const title = loginHeader.querySelector('.customer-login__title');
      const subtitle = loginHeader.querySelector('.customer-login__subtitle');
      
      if (title) title.textContent = 'Reset your password';
      if (subtitle) subtitle.textContent = 'We will send you an email to reset your password';
    }
    
    // Create reset form
    createPasswordResetForm();
  }

  /**
   * Create password reset form
   */
  function createPasswordResetForm() {
    // Remove existing reset form if any
    const existingResetForm = document.querySelector('.password-reset-form');
    if (existingResetForm) {
      existingResetForm.remove();
    }
    
    // Create reset form container
    const resetFormContainer = document.createElement('div');
    resetFormContainer.className = 'password-reset-form';
    resetFormContainer.style.cssText = `
      max-width: 400px;
      margin: 0 auto;
      padding: 20px;
    `;
    
    // Create form with proper Shopify form structure
    const form = document.createElement('form');
    form.id = 'password-reset-form';
    form.method = 'POST';
    form.action = '/account/recover';
    form.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 16px;
    `;
    
    // Add hidden form type field (required by Shopify)
    const formTypeInput = document.createElement('input');
    formTypeInput.type = 'hidden';
    formTypeInput.name = 'form_type';
    formTypeInput.value = 'recover_customer_password';
    form.appendChild(formTypeInput);
    
    // Email input
    const emailField = document.createElement('div');
    emailField.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;
    
    const emailLabel = document.createElement('label');
    emailLabel.textContent = '';
    emailLabel.style.cssText = `
      font-weight: 500;
      color: #333;
    `;
    
    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.name = 'email';
    emailInput.id = 'RecoverEmail';
    emailInput.placeholder = 'Enter your email';
    emailInput.required = true;
    emailInput.autocomplete = 'email';
    emailInput.autocorrect = 'off';
    emailInput.autocapitalize = 'off';
    emailInput.style.cssText = `
      padding: 12px 16px;
      border: 1px solid #ddd;
      border-radius: 30px;
      font-size: 16px;
      outline: none;
      transition: border-color 0.2s ease;
    `;
    
    emailInput.addEventListener('focus', function() {
      this.style.borderColor = '#B07C83';
    });
    
    emailInput.addEventListener('blur', function() {
      this.style.borderColor = '#ddd';
    });
    
    emailField.appendChild(emailLabel);
    emailField.appendChild(emailInput);
    
    // Submit button
    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.textContent = 'Submit';
    submitButton.style.cssText = `
      padding: 12px 24px;
      background: #B07C83;
      color: white;
      border: none;
      border-radius: 30px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease;
    `;
    
    submitButton.addEventListener('mouseenter', function() {
      this.style.background = '#9a6b72';
    });
    
    submitButton.addEventListener('mouseleave', function() {
      this.style.background = '#B07C83';
    });
    
    // Cancel link
    const cancelLink = document.createElement('a');
    cancelLink.href = '#';
    cancelLink.textContent = 'Cancel';
    cancelLink.style.cssText = `
      text-align: center;
    text-decoration: underline;
    color: black;
    font-style: italic;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 8px;
    `;
    
    cancelLink.addEventListener('click', function(e) {
      e.preventDefault();
      // Refresh the page to reset everything
      window.location.reload();
    });
    
    // Form submission - let the form submit naturally to Shopify
    form.addEventListener('submit', function(e) {
      const email = emailInput.value.trim();
      if (!email) {
        e.preventDefault();
        alert('Please enter your email address');
        return;
      }
      
      // Show loading state
      submitButton.textContent = 'Sending...';
      submitButton.disabled = true;
      
      // Let the form submit naturally - don't prevent default
      // Shopify will handle the form submission and redirect back
    });
    
    // Assemble form
    form.appendChild(emailField);
    form.appendChild(submitButton);
    resetFormContainer.appendChild(form);
    resetFormContainer.appendChild(cancelLink);
    
    // Insert after header
    const header = document.querySelector('.customer-login__header');
    if (header && header.parentNode) {
      header.parentNode.insertBefore(resetFormContainer, header.nextSibling);
    }
  }

  /**
   * Hide password reset form and show login form
   */
  function hidePasswordResetForm() {
    // Remove hash from URL
    if (window.location.hash.includes('#recover')) {
      window.location.hash = '';
    }
    
    // Remove reset form
    const resetForm = document.querySelector('.password-reset-form');
    if (resetForm) {
      resetForm.remove();
    }
    
    // Show login form
    const loginForm = document.querySelector('.customer-login__form');
    const loginActions = document.querySelector('.customer-login__actions');
    const loginLinks = document.querySelector('.customer-login__links');
    
    if (loginForm) loginForm.style.display = 'block';
    if (loginActions) loginActions.style.display = 'block';
    if (loginLinks) loginLinks.style.display = 'flex';
    
    // Reset header
    const loginHeader = document.querySelector('.customer-login__header');
    if (loginHeader) {
      const title = loginHeader.querySelector('.customer-login__title');
      const subtitle = loginHeader.querySelector('.customer-login__subtitle');
      
      if (title) title.textContent = 'Sign In';
      if (subtitle) subtitle.textContent = 'Welcome back! Please sign in to your account.';
    }
  }

  /**
   * Show login actions and links
   */
  function showLoginActionsAndLinks() {
    const loginActions = document.querySelectorAll('.customer-login__actions');
    const loginLinks = document.querySelectorAll('.customer-login__links');
    
    loginActions.forEach(action => {
      action.style.display = 'block';
    });
    
    loginLinks.forEach(link => {
      link.style.display = 'flex';
    });
    
    console.log('Login actions and links displayed');
  }

  /**
   * Hide login actions and links
   */
  function hideLoginActionsAndLinks() {
    const loginActions = document.querySelectorAll('.customer-login__actions');
    const loginLinks = document.querySelectorAll('.customer-login__links');
    
    loginActions.forEach(action => {
      action.style.display = 'none';
    });
    
    loginLinks.forEach(link => {
      link.style.display = 'none';
    });
    
    console.log('Login actions and links hidden');
  }

})();
